import dayjs from 'dayjs'
import { JiraCreateField } from 'models'

export type JiraFieldKind =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'user'
  | 'multiuser'
  | 'labels'

export interface FieldOption {
  label: string
  value: string
}

/* Set on the Destination step or sent outside `fields`, so never rendered as a Jira field. */
const WIZARD_OWNED_FIELDS = new Set(['project', 'issuetype', 'summary', 'description', 'attachment', 'issuelinks'])

const TEXTAREA_CUSTOM_TYPES = ['textarea']

/* Options are sent back as `{id}`, so values without one (Service Desk's request language) can't be picked. */
const hasIdOptions = (field: JiraCreateField) => !!field.allowed_values?.some((v) => v?.id != null)

/* The input for a field, read from Jira's schema; null when the wizard has no input for it. */
export const fieldKind = (field: JiraCreateField): JiraFieldKind | null => {
  const { type, items, system, custom = '' } = field.schema || ({} as JiraCreateField['schema'])
  if (type === 'array') {
    if (items === 'user') return 'multiuser'
    if (items === 'string' && !field.allowed_values) return 'labels'
    if (hasIdOptions(field)) return 'multiselect'
    return null
  }
  if (type === 'user') return 'user'
  if (type === 'date') return 'date'
  if (type === 'datetime') return 'datetime'
  if (type === 'number') return 'number'
  if (type === 'string') {
    if (field.allowed_values) return hasIdOptions(field) ? 'select' : null
    return system === 'environment' || TEXTAREA_CUSTOM_TYPES.some((t) => custom.endsWith(t)) ? 'textarea' : 'text'
  }
  return hasIdOptions(field) ? 'select' : null
}

export const isWizardField = (field: JiraCreateField) => !WIZARD_OWNED_FIELDS.has(field.id) && !!fieldKind(field)

/* Jira's allowed values name themselves with `name` (priority, version) or `value` (custom options). */
export const optionsOf = (field: JiraCreateField): FieldOption[] =>
  (field.allowed_values || [])
    .filter((v) => v?.id != null && v.disabled !== true)
    .map((v) => ({ label: String(v.name ?? v.value ?? v.label ?? v.id), value: String(v.id) }))

export const isEmptyValue = (value: unknown) =>
  value == null || (typeof value === 'string' && !value.trim()) || (Array.isArray(value) && value.length === 0)

/* The wizard's value for a field before the user touches it: Jira's default, else empty. */
export const initialValueOf = (field: JiraCreateField): any => {
  const kind = fieldKind(field)
  const multi = kind === 'multiselect' || kind === 'multiuser' || kind === 'labels'
  const d = field.default_value
  if (d == null) return multi ? [] : ''
  if (kind === 'select') return String(d.id ?? d.value ?? '')
  if (kind === 'multiselect') return Array.isArray(d) ? d.map((v) => String(v.id ?? v.value)) : []
  if (kind === 'labels') return Array.isArray(d) ? d.map(String) : []
  if (kind === 'user') return String(d.accountId ?? '')
  if (typeof d === 'object') return multi ? [] : ''
  return d
}

/* Jira's v3 API rejects plain strings for rich-text fields; each line becomes an ADF paragraph. */
const toAdf = (text: string) => ({
  type: 'doc',
  version: 1,
  content: text.split(/\r?\n/).map((line) => ({
    type: 'paragraph',
    content: line ? [{ type: 'text', text: line }] : []
  }))
})

/* A field's wizard value in the form Jira's create API expects it. */
export const toJiraValue = (field: JiraCreateField, value: any): any => {
  switch (fieldKind(field)) {
    case 'select':
      return { id: value }
    case 'multiselect':
      return (value as string[]).map((id) => ({ id }))
    case 'user':
      return { accountId: value }
    case 'multiuser':
      return (value as string[]).map((accountId) => ({ accountId }))
    case 'number':
      return Number(value)
    case 'textarea':
      return toAdf(String(value).trim())
    case 'datetime':
      return dayjs(value).format('YYYY-MM-DDTHH:mm:ss.SSSZZ')
    default:
      return value
  }
}

export interface TicketDateRange {
  start: string | null
  end: string | null
}

const START_COLUMN = /\bstart/i
const END_COLUMN = /\b(closed?|end)/i
const TARGET_START_FIELD = /^target start$/i
const TARGET_END_FIELD = /^target end$/i

/* The span the selected rows cover: earliest value of the grid's start date column, latest of its close one. */
export const recordDateRange = (
  rows: Record<string, any>[],
  columns: { field?: string; headerName?: string; filter?: any }[]
): TicketDateRange => {
  const datesOf = (header: RegExp) => {
    const column = columns.find((c) => c.field && c.filter === 'agDateColumnFilter' && header.test(c.headerName ?? ''))
    if (!column) return []
    return rows
      .map((row) => row?.[column.field as string])
      .filter((v) => v != null && v !== '')
      .map((v) => dayjs(v))
      .filter((d) => d.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf())
  }
  const starts = datesOf(START_COLUMN)
  const ends = datesOf(END_COLUMN)
  return {
    start: starts.length ? starts[0].format('YYYY-MM-DD') : null,
    end: ends.length ? ends[ends.length - 1].format('YYYY-MM-DD') : null
  }
}

/* Jira's Target start/end take the records' dates unless Jira already defaults them. */
export const prefillTargetDates = (
  fields: JiraCreateField[],
  values: Record<string, any>,
  range?: TicketDateRange
): Record<string, any> => {
  if (!range) return values
  const next = { ...values }
  fields.forEach((field) => {
    const date = TARGET_START_FIELD.test(field.label) ? range.start : TARGET_END_FIELD.test(field.label) ? range.end : null
    if (date && field.id in next && isEmptyValue(next[field.id])) next[field.id] = date
  })
  return next
}

export const buildJiraFields =(fields: JiraCreateField[], values: Record<string, any>): Record<string, any> =>
  fields.reduce<Record<string, any>>((acc, field) => {
    const value = values[field.id]
    if (isWizardField(field) && !isEmptyValue(value)) acc[field.id] = toJiraValue(field, value)
    return acc
  }, {})
